/**
 * Library scan coordinator — singleton that owns an in-flight library scan
 * independently of any React component's lifetime.
 *
 * v2 — Streaming batches
 * ----------------------
 * The Connector now emits `scan-batch` Tauri events as it scans, each
 * containing ~500 tracks. The coordinator ingests each batch to the backend
 * as it arrives (with skip_rebuild=true), so albums start appearing in the
 * grid within the first minute — not after 25 minutes. When the scan
 * finishes, one `rebuildAlbums()` call materializes the full album table
 * and the library store cache is invalidated so the grid refreshes.
 */

import {
  scanLibrary,
  scanLocalLibrarySources,
  cancelScan as nativeCancelScan,
  onScanProgress,
  onScanBatch,
  onLocalScanProgress,
  getMachineId,
  setLibraryPaths,
  enableHomeLibraryRemoteAccess,
  setAuthToken,
} from '../lib/connector';
import type { ScannedTrack, ScanSummary } from '../lib/connector';
import { libraryService, probeLocalLibraryIpc } from './libraryService';
import { isRemoteLibraryViewer, probeLocalLibraryHost } from './localLibraryIpcGate';
import { useOperationProgressStore } from '../store/operationProgressStore';
import { useLibraryCacheStore } from '../store/libraryCacheStore';
import { useLibraryStore } from '../store/libraryStore';
import {
  collapseBulkScanPlan,
  dedupeTargetedScanSources,
  findEnclosingSource,
  isTargetedScanPlan,
  MULTI_SELECT_SCAN_THRESHOLD,
  normScanPath,
} from './scanPlanUtils';
import { saveScanBaseline } from './scanTimingEstimate';

export type ScanPhase =
  | 'idle'
  | 'scanning'
  | 'ingesting'
  | 'finalizing'
  | 'done'
  | 'cancelled'
  | 'error';

export interface ScanProgress {
  files_found?: number;
  albums_identified?: number;
  percent?: number;
  eta_seconds?: number;
  current_file?: string;
  phase?: string;
}

export interface ScanSource {
  id: string;
  /** Registered library source root path. */
  path: string;
  /** When set, only walk/tag files under this subfolder (must be inside `path`). */
  scan_path?: string;
}

export interface ScanState {
  phase: ScanPhase;
  selectedPaths: string[];
  scanSources?: ScanSource[];
  machineId?: string;
  nativeProgress: ScanProgress | null;
  ingestTotal: number;
  ingestDone: number;
  trackCount: number;
  albumCount: number;
  error: string | null;
  active: boolean;
}

type Listener = (state: ScanState) => void;

const INGEST_BATCH_SIZE = 200;

const INITIAL_STATE: ScanState = {
  phase: 'idle',
  selectedPaths: [],
  machineId: undefined,
  nativeProgress: null,
  ingestTotal: 0,
  ingestDone: 0,
  trackCount: 0,
  albumCount: 0,
  error: null,
  active: false,
};

let state: ScanState = { ...INITIAL_STATE };
const listeners = new Set<Listener>();
let unlistenNative: (() => void) | null = null;
let unlistenBatch: (() => void) | null = null;
let currentRun: Promise<void> | null = null;
let cancelRequested = false;
/** Snapshot of sources for the active run — avoids stale reads from `state`. */
let activeRunScanSources: ScanSource[] | undefined;

// Tracks ingested from streaming batches — count maintained across the scan
let streamIngestedCount = 0;
let scanOperationId: string | null = null;
let unlistenLocalProgress: (() => void) | null = null;

interface ActiveScanTiming {
  baselineKey: string;
  walkStartedAt: number;
  tagPhaseStartedAt: number;
  walkEntriesPeak: number;
  lastPhase: string;
}
let activeScanTiming: ActiveScanTiming | null = null;

function resolveScanBaselineKey(opts: {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  prebuiltSources?: ScanSource[];
}): string {
  const src = opts.prebuiltSources?.[0] ?? opts.scanSources?.[0];
  if (src) return src.scan_path || src.path;
  return opts.selectedPaths[0] ?? '';
}

function isTauriConnector(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { __TAURI__?: unknown; __TAURI_INTERNALS__?: unknown };
  return !!(w.__TAURI__ || w.__TAURI_INTERNALS__);
}

function emit(): void {
  for (const l of listeners) {
    try { l(state); } catch { /* listener errors must not break the scan */ }
  }
}

function setState(patch: Partial<ScanState>): void {
  state = { ...state, ...patch };
  emit();
}

export function subscribeScan(listener: Listener): () => void {
  listeners.add(listener);
  try { listener(state); } catch {}
  return () => { listeners.delete(listener); };
}

export function getScanState(): ScanState {
  return state;
}

export function isScanActive(): boolean {
  return state.active;
}

export function startScan(opts: {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  /** Sources already registered via local_library_add_source — skip duplicate creation. */
  prebuiltSources?: ScanSource[];
  isTemporary?: boolean;
}): Promise<void> {
  if (currentRun) {
    console.log(`[Scan] scan already in progress, ignoring duplicate request ts=${new Date().toISOString()}`);
    return currentRun;
  }

  if (typeof window !== 'undefined' && window.__TAURI__) {
    if (isRemoteLibraryViewer()) {
      console.warn('[Scan] blocked — remote library viewer cannot add local sources');
      return Promise.reject(new Error('Add Music is only available on the Mac where your library lives.'));
    }
  }

  const collapsed = collapseBulkScanPlan({
    selectedPaths: opts.selectedPaths,
    scanSources: opts.scanSources,
    prebuiltSources: opts.prebuiltSources,
  });
  opts = { ...opts, ...collapsed };

  const isRescan = !!(opts.scanSources && opts.scanSources.length > 0);
  const prebuiltCount = opts.prebuiltSources?.length || 0;
  console.log(
    `[Scan] ${isRescan ? 're-scan' : 'fresh scan'} requested, current state: ${state.phase}, ` +
    `paths=${opts.selectedPaths.length}, sources=${opts.scanSources?.length || 0}, ` +
    `prebuilt=${prebuiltCount} ts=${new Date().toISOString()}`,
  );

  cancelRequested = false;
  streamIngestedCount = 0;
  // Bust version immediately so parallel grid fetches cannot cache-hit mid-scan.
  try {
    useLibraryCacheStore.getState().setVersion(null);
  } catch { /* cache store optional */ }
  probeLocalLibraryIpc().then((ipcOk) => {
    if (!ipcOk) return;
    try {
      useLibraryCacheStore.getState().setVersion(null);
      localStorage.removeItem('maestro:libraryCache');
      window.dispatchEvent(new CustomEvent('maestro:library:scan-started'));
    } catch { /* cache store optional */ }
  }).catch(() => {});
  if (scanOperationId) {
    useOperationProgressStore.getState().endOperation(scanOperationId);
    scanOperationId = null;
  }
  const baselineKey = resolveScanBaselineKey(opts);
  activeScanTiming = {
    baselineKey,
    walkStartedAt: Date.now(),
    tagPhaseStartedAt: 0,
    walkEntriesPeak: 0,
    lastPhase: '',
  };
  const now = Date.now();
  scanOperationId = useOperationProgressStore.getState().beginOperation('Preparing scan…', {
    progress: 0,
    phase: 'enumerating',
    baselineKey,
    phaseStartedAt: now,
  });
  const initialSources = opts.prebuiltSources?.length
    ? opts.prebuiltSources
    : opts.scanSources;
  activeRunScanSources = initialSources?.length ? [...initialSources] : undefined;
  state = {
    ...INITIAL_STATE,
    phase: 'scanning',
    selectedPaths: opts.selectedPaths,
    scanSources: initialSources,
    active: true,
  };
  emit();

  currentRun = runScan().finally(() => {
    currentRun = null;
    activeRunScanSources = undefined;
  });
  return currentRun;
}

function applyLocalProgressToOperation(p: Record<string, unknown>): void {
  if (!scanOperationId) return;
  const phase = String(p.phase ?? '');
  const rawCurrent = typeof p.current_file === 'number' ? p.current_file : null;
  const rawTotal = typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null;
  const total = rawTotal != null ? Math.max(rawTotal, rawCurrent ?? 0) : null;
  const current = rawCurrent != null && total != null
    ? Math.min(Math.max(0, rawCurrent), total)
    : rawCurrent;
  const pathHint = typeof p.current_path === 'string' ? p.current_path : null;
  const sourceIndex = typeof p.source_index === 'number' ? p.source_index : null;
  const sourcesTotal = typeof p.sources_total === 'number' ? p.sources_total : null;
  const multiSource = sourcesTotal != null && sourcesTotal > 1 && sourceIndex != null;
  const folderHint = (phase === 'enumerating' || phase === 'discovering') && multiSource
    ? `Folder ${sourceIndex} of ${sourcesTotal}`
    : null;
  const etaMs = typeof p.eta_ms === 'number' && p.eta_ms >= 0 ? p.eta_ms : null;
  const filesPerSecond = typeof p.files_per_second === 'number' && p.files_per_second > 0
    ? p.files_per_second
    : null;

  if (activeScanTiming) {
    if ((phase === 'enumerating' || phase === 'discovering') && typeof current === 'number') {
      activeScanTiming.walkEntriesPeak = Math.max(activeScanTiming.walkEntriesPeak, current);
    }
    if (phase === 'scanning' && activeScanTiming.lastPhase !== 'scanning') {
      activeScanTiming.tagPhaseStartedAt = Date.now();
    }
    activeScanTiming.lastPhase = phase;
  }

  let label = 'Preparing scan…';
  let progress: number | undefined;
  let totalSteps: number | undefined;
  let currentStep: number | undefined;
  let opPhase = phase || 'scanning';

  if (multiSource) {
    const slot = 1 / sourcesTotal!;
    const base = (sourceIndex! - 1) * slot;
    const within = phase === 'scanning' && total != null && current != null && total > 0
      ? (current / total) * slot
      : phase === 'enumerating'
        ? slot * 0.35
        : 0;
    progress = Math.min(0.98, base + within);
    totalSteps = sourcesTotal!;
    currentStep = sourceIndex!;
  }

  if (phase === 'enumerating' || phase === 'discovering') {
    label = folderHint
      ? `${folderHint} — walking NAS folders…`
      : current != null && current > 0
        ? `Walking NAS folders… ${current.toLocaleString()} items found`
        : 'Walking NAS folders…';
    if (current != null && current > 0) currentStep = current;
    opPhase = 'enumerating';
  } else if (phase === 'scanning') {
    label = total != null && current != null
      ? `${folderHint ? `${folderHint} — ` : ''}Reading tags… ${current.toLocaleString()} of ${total.toLocaleString()} files`
      : folderHint
        ? `${folderHint} — reading tags…`
        : 'Reading tags from files (embedded metadata first)…';
    if (!multiSource && total != null && current != null) {
      progress = Math.min(1, current / total);
      totalSteps = total;
      currentStep = current;
    }
    opPhase = 'scanning';
  } else if (phase === 'done') {
    label = 'Building album library…';
    progress = 0.98;
    opPhase = 'finalizing';
  } else if (phase === 'cancelled') {
    label = 'Scan cancelled';
    opPhase = 'cancelled';
    progress = 1;
  } else if (phase === 'error') {
    label = typeof p.error === 'string' ? `Scan error: ${p.error}` : 'Scan error';
    opPhase = 'error';
    progress = 1;
  }

  const elapsedMs = typeof p.elapsed_ms === 'number' && p.elapsed_ms >= 0 ? p.elapsed_ms : undefined;

  const existing = useOperationProgressStore.getState().operations[scanOperationId];
  const phaseChanged = existing?.phase !== opPhase;

  useOperationProgressStore.getState().updateOperation(scanOperationId, {
    label,
    phase: opPhase,
    progress,
    totalSteps,
    currentStep,
    currentLabel: pathHint,
    etaMs: etaMs ?? undefined,
    elapsedMs,
    filesPerSecond: filesPerSecond ?? undefined,
    baselineKey: activeScanTiming?.baselineKey,
    phaseStartedAt: phaseChanged ? Date.now() : existing?.phaseStartedAt,
  });
}

async function finishScanSuccess(
  summary: { total_tracks: number; total_albums?: number },
  ingestFailed = false,
  opts?: { localCore?: boolean },
): Promise<void> {
  const localCore = opts?.localCore === true;
  setState({ phase: 'finalizing' });
  if (scanOperationId) {
    useOperationProgressStore.getState().updateOperation(scanOperationId, {
      label: 'Updating album library…',
      phase: 'finalizing',
      progress: 0.95,
    });
  }

  const albums = await fetchRealAlbumCount(summary.total_albums ?? 0);

  if (!localCore) {
    try {
      if (scanOperationId) {
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: 'Filling in missing artwork and credits (only where tags are empty)…',
          phase: 'finalizing',
          progress: 0.97,
        });
      }
      const mod = await import('../store/maestroStore');
      mod.useMaestroStore.getState().triggerEnrichment?.();
    } catch { /* enrichment is optional */ }
  }

  invalidateLibraryStore();

  const doneLabel = ingestFailed
    ? `Scan finished with errors — ${albums} albums, ${summary.total_tracks} tracks (some uploads failed)`
    : `Scan complete — ${albums} album${albums !== 1 ? 's' : ''}, ${summary.total_tracks.toLocaleString()} tracks`;

  if (activeScanTiming?.baselineKey) {
    const now = Date.now();
    const tagStarted = activeScanTiming.tagPhaseStartedAt;
    saveScanBaseline(activeScanTiming.baselineKey, {
      walkEntries: activeScanTiming.walkEntriesPeak,
      tagFiles: summary.total_tracks,
      walkMs: tagStarted > activeScanTiming.walkStartedAt
        ? tagStarted - activeScanTiming.walkStartedAt
        : now - activeScanTiming.walkStartedAt,
      tagMs: tagStarted > 0 ? now - tagStarted : 0,
    });
    activeScanTiming = null;
  }

  // Clear active BEFORE grid reload — loadAlbums() defers while isScanActive().
  setState({
    phase: 'done',
    trackCount: summary.total_tracks,
    albumCount: albums,
    active: false,
  });

  // Brief pause so SQLite handles are released before the grid reads albums.
  await new Promise((r) => setTimeout(r, localCore ? 200 : 500));
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { reason: 'scan-complete', forceFullRefresh: true },
    }));
  } catch { /* ignore */ }

  if (scanOperationId) {
    const doneId = scanOperationId;
    useOperationProgressStore.getState().updateOperation(doneId, {
      label: doneLabel,
      phase: 'done',
      progress: 1,
    });
    scanOperationId = null;
    window.setTimeout(() => {
      useOperationProgressStore.getState().endOperation(doneId);
    }, 3000);
  }

  // Remote access / daemon setup can take 30–45s — never block the grid after scan.
  void setupPostScanRemoteAccess(localCore).catch((e) => {
    console.debug('[scan-coordinator] post-scan remote access setup failed:', e);
  });
}

/** Best-effort tunnel + daemon registration after a successful scan. */
async function setupPostScanRemoteAccess(localCore: boolean): Promise<void> {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  if (localStorage.getItem('maestro_remote_access') === '0') return;

  try {
    if (!localCore) {
      let allPaths = [...state.selectedPaths];
      try {
        const resp = await libraryService.getSources();
        const sourcePaths = (resp?.sources || []).map((s: { path?: string }) => s.path).filter(Boolean);
        allPaths = [...new Set([...allPaths, ...sourcePaths])];
      } catch { /* best-effort */ }
      await setLibraryPaths(allPaths);
    }
    const connector = await import('../lib/connector');
    await connector.warmLocalNetworkAccess();
    await connector.enableHomeLibraryRemoteAccess();
  } catch (e) {
    console.debug('[scan-coordinator] post-scan remote access setup failed:', e);
  }
}

export async function requestCancelScan(): Promise<void> {
  cancelRequested = true;
  if (scanOperationId) {
    useOperationProgressStore.getState().updateOperation(scanOperationId, { label: 'Cancelling scan...' });
  }
  try { await nativeCancelScan(); } catch { /* best-effort */ }
}

export function resetScan(): void {
  if (currentRun) {
    console.warn('[scan-coordinator] resetScan() called while scan is active — ignored');
    return;
  }
  state = { ...INITIAL_STATE };
  if (scanOperationId) {
    useOperationProgressStore.getState().endOperation(scanOperationId);
    scanOperationId = null;
  }
  emit();
}

export async function rescanSources(sourceIds: string[]): Promise<void> {
  if (!sourceIds.length) return;
  const resp = await libraryService.getSources();
  const allSources: Array<{ id?: string; path?: string }> = resp?.sources || [];
  const matching = allSources.filter((s) => sourceIds.includes(s.id || ''));
  const paths = matching.map((s) => s.path).filter(Boolean) as string[];
  if (!paths.length) return;

  const scanSources: ScanSource[] = matching
    .filter((s): s is { id: string; path: string } => !!(s.id && s.path))
    .map((s) => ({ id: s.id, path: s.path }));

  console.log(
    `[Scan] re-scan requested: ${sourceIds.length} source(s), ` +
    `${scanSources.length} with IDs, ${paths.length} paths ts=${new Date().toISOString()}`,
  );

  return startScan({ selectedPaths: paths, scanSources });
}

// Ingest a chunk of tracks to the backend, with retry.
// skip_rebuild=true tells the backend to skip per-folder album materialization.
async function ingestChunk(
  tracks: ScannedTrack[],
  sourceId: string | null,
  machineId: string | undefined,
): Promise<number> {
  let failedBatches = 0;
  for (let i = 0; i < tracks.length; i += INGEST_BATCH_SIZE) {
    const batch = tracks.slice(i, i + INGEST_BATCH_SIZE);
    let attempt = 0;
    let succeeded = false;
    for (;;) {
      try {
        await libraryService.ingestTracks(batch, sourceId, machineId, true);
        succeeded = true;
        break;
      } catch (e: any) {
        attempt += 1;
        if (attempt >= 3) {
          console.warn(
            `[scan-coordinator] ingest batch ${i}..${i + batch.length} failed after 3 attempts:`,
            e?.message || e,
          );
          break;
        }
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
    if (!succeeded) failedBatches++;
    streamIngestedCount += batch.length;
    setState({ ingestDone: streamIngestedCount });
  }
  return failedBatches;
}

async function resolveScanSourcesForRun(sources: ScanSource[]): Promise<ScanSource[]> {
  if (isTargetedScanPlan(sources)) {
    return dedupeTargetedScanSources(sources);
  }

  let next = sources;
  if (sources.length >= MULTI_SELECT_SCAN_THRESHOLD) {
    const collapsed = collapseBulkScanPlan({
      selectedPaths: sources.map((s) => s.scan_path || s.path),
      scanSources: sources,
    });
    next = collapsed.scanSources || sources;
  }

  const hasSubfolderScan = next.some((s) => {
    const sub = s.scan_path ? normScanPath(s.scan_path) : '';
    const root = normScanPath(s.path);
    return sub && sub !== root;
  });
  if (hasSubfolderScan || next.length !== 1) return next;

  const sole = next[0];
  const scanTarget = normScanPath(sole.scan_path || sole.path);
  const sourceRoot = normScanPath(sole.path);
  if (scanTarget === sourceRoot) return next;

  try {
    const resp = await libraryService.getSources();
    const registered = (resp?.sources || []) as Array<{ id?: string; path?: string }>;
    const enclosing = findEnclosingSource([scanTarget], registered);
    if (enclosing) {
      console.log(`[Scan] Using registered library root ${enclosing.path} for subfolder scan`);
      return [{ id: enclosing.id, path: enclosing.path, scan_path: scanTarget }];
    }
    const exact = registered.find((s) => normScanPath(s.path || '') === scanTarget);
    if (exact?.id) {
      return [{ id: exact.id, path: scanTarget }];
    }
    const label = scanTarget.split('/').filter(Boolean).pop() || 'Music';
    const created = await libraryService.createSource({ path: scanTarget, label });
    const id = created?.source?.id;
    if (id) {
      console.log(`[Scan] Registered library root ${scanTarget} for scan`);
      return [{ id, path: scanTarget }];
    }
  } catch (e) {
    console.warn('[Scan] scan source resolve failed — using plan as-is', e);
  }
  return next;
}

async function runLocalCoreScan(): Promise<void> {
  try {
    let scanSources = activeRunScanSources?.length
      ? [...activeRunScanSources]
      : state.scanSources;
    if (scanSources?.length) {
      scanSources = await resolveScanSourcesForRun(scanSources);
      scanSources = dedupeTargetedScanSources(scanSources);
      activeRunScanSources = scanSources;
      setState({
        scanSources,
        selectedPaths: scanSources.map((s) => s.scan_path || s.path),
      });
    }
    if (!scanSources?.length) {
      const built: ScanSource[] = [];
      for (const p of state.selectedPaths) {
        const label = p.replace(/\\/g, '/').split('/').filter(Boolean).pop() || 'Music';
        try {
          const resp = await libraryService.createSource({ path: p, label, is_temporary: false });
          const id = resp?.source?.id;
          const path = resp?.source?.path || p;
          if (id) built.push({ id, path });
        } catch {
          // may already exist
        }
      }
      scanSources = built;
      setState({ scanSources: built });
      console.log(
        `[Scan] local-core: registered ${built.length} source(s) ts=${new Date().toISOString()}`,
      );
    } else {
      console.log(
        `[Scan] local-core: using ${scanSources.length} pre-registered source(s) ts=${new Date().toISOString()}`,
      );
    }

    if (!scanSources?.length) {
      throw new Error('No library sources to scan');
    }

    unlistenLocalProgress = await onLocalScanProgress((p) => {
      setState({ nativeProgress: p as ScanProgress });
      applyLocalProgressToOperation(p);
    });

    console.log(
      `[Scan] local-core scan started for ${scanSources.length} folder(s) ts=${new Date().toISOString()}`,
    );
    for (const s of scanSources) {
      const walk = s.scan_path || s.path;
      console.log(
        `[Scan] walk target: ${walk}${s.scan_path && s.scan_path !== s.path ? ` (library root: ${s.path})` : ''}`,
      );
    }
    const summary = await scanLocalLibrarySources(scanSources);

    if (summary.skipped_sources?.length && summary.skipped_sources.length >= scanSources.length) {
      throw new Error(
        'No folders were scanned — library paths may be offline or not registered. ' +
        'Check that /Volumes/share is mounted, then try again.',
      );
    }
    if (unlistenLocalProgress) {
      unlistenLocalProgress();
      unlistenLocalProgress = null;
    }

    if (cancelRequested) {
      setState({ phase: 'cancelled', active: false });
      invalidateLibraryStore();
      if (scanOperationId) {
        const cancelledId = scanOperationId;
        useOperationProgressStore.getState().updateOperation(cancelledId, {
          label: 'Scan cancelled',
          phase: 'cancelled',
          progress: 1,
        });
        scanOperationId = null;
        window.setTimeout(() => {
          useOperationProgressStore.getState().endOperation(cancelledId);
        }, 3000);
      }
      return;
    }

    const newOrChanged = summary.files_new_or_changed ?? 0;
    const unchanged = summary.files_unchanged ?? 0;
    console.log(
      `[Scan] local-core scan complete: ${newOrChanged} new/changed file(s), ` +
      `${unchanged} unchanged, ${summary.total_albums ?? '?'} albums in library ts=${new Date().toISOString()}`,
    );
    if (newOrChanged === 0 && (state.selectedPaths?.length || scanSources.length)) {
      console.warn(
        '[Scan] No new or changed audio files under the scanned folder(s). ' +
        'If you added a DSD/DSF file earlier, run Scan again — unchanged files with missing tags are re-processed automatically. ' +
        'Otherwise put music inside a watched folder (Browse Folders → Add to Library).',
      );
    }
    await finishScanSuccess(summary, false, { localCore: true });
  } catch (err: any) {
    console.error('[Scan] local-core scan failed:', err?.message || err, err);
    if (unlistenLocalProgress) {
      try { unlistenLocalProgress(); } catch {}
      unlistenLocalProgress = null;
    }
    setState({
      phase: 'error',
      error: err?.message || 'Scan failed',
      active: false,
    });
    if (scanOperationId) {
      const errId = scanOperationId;
      useOperationProgressStore.getState().updateOperation(errId, {
        label: err?.message || 'Scan failed',
        phase: 'error',
        progress: 1,
      });
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(errId);
      }, 4000);
    }
  }
}

async function runScan(): Promise<void> {
  await probeLocalLibraryHost().catch(() => {});
  const useLocalCore = isTauriConnector() && (await probeLocalLibraryIpc());
  console.log(
    `[Scan] engine: ${useLocalCore ? 'local-core (SQLite)' : 'legacy cloud ingest'} ts=${new Date().toISOString()}`,
  );
  if (useLocalCore) {
    await runLocalCoreScan();
    return;
  }

  let sourceId: string | null = null;
  const isRescan = !!(state.scanSources && state.scanSources.length > 0);

  try {
    // 0. Push current access token to Connector so scan/reconcile commands authenticate
    try {
      const stored = localStorage.getItem('maestro_auth');
      const token = stored ? JSON.parse(stored).accessToken : null;
      if (token) await setAuthToken(token);
    } catch { /* best-effort */ }

    // 1. Machine ID
    let machineId: string | undefined;
    try { machineId = await getMachineId(); } catch { machineId = undefined; }
    if (machineId) {
      try { localStorage.setItem('maestro_machine_id', machineId); } catch {}
    }
    setState({ machineId });

    // 2. Subscribe to native progress events
    unlistenNative = await onScanProgress((p: ScanProgress) => {
      setState({ nativeProgress: p });
      if (scanOperationId) {
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: p?.phase === 'scan-complete' ? 'Finalizing scan...' : 'Scanning...',
          progress: typeof p?.percent === 'number' ? p.percent / 100 : undefined,
          totalSteps: typeof p?.files_found === 'number' ? p.files_found : undefined,
          currentStep: typeof p?.files_processed === 'number' ? p.files_processed : undefined,
        });
      }
    });

    // 3. Resolve source IDs (pre-built from Finder, or create for cloud ingest path)
    if (state.scanSources && state.scanSources.length > 0) {
      sourceId = state.scanSources[0].id;
      console.log(
        `[Scan] using ${state.scanSources.length} existing source ID(s), ` +
        `primary=${sourceId} ts=${new Date().toISOString()}`,
      );
    } else {
      const sourceIds: string[] = [];
      for (const p of state.selectedPaths) {
        const label =
          p.replace(/\\/g, '/').split('/').filter(Boolean).pop() || 'Music';
        try {
          const resp = await libraryService.createSource({
            path: p,
            label,
            is_temporary: false,
            machine_id: machineId,
          });
          if (resp?.source?.id) sourceIds.push(resp.source.id);
        } catch {
          // source may already exist — continue
        }
      }
      sourceId = sourceIds[0] || null;
      console.log(
        `[Scan] fresh scan: created ${sourceIds.length} source(s), ` +
        `primary=${sourceId || '(none)'} ts=${new Date().toISOString()}`,
      );
    }

    // 4. Subscribe to scan-batch events — ingest each batch as it arrives
    setState({ phase: 'scanning', ingestTotal: 0, ingestDone: 0 });
    let totalFailedBatches = 0;

    // Batch ingestion runs as fire-and-forget promises queued sequentially
    let ingestChain = Promise.resolve();

    unlistenBatch = await onScanBatch((tracks: ScannedTrack[]) => {
      if (!tracks || tracks.length === 0) return;
      setState({
        ingestTotal: (state.ingestTotal || 0) + tracks.length,
        phase: 'ingesting',
      });
      if (scanOperationId) {
        const nextTotal = (state.ingestTotal || 0) + tracks.length;
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: 'Ingesting scan results...',
          totalSteps: nextTotal,
        });
      }
      ingestChain = ingestChain.then(async () => {
        const failed = await ingestChunk(tracks, sourceId, machineId);
        totalFailedBatches += failed;
      });
    });

    // 5. Start the scan — pass source IDs when available for incremental scanning
    let summary: ScanSummary;
    const scanSources = state.scanSources?.length ? state.scanSources : undefined;
    console.log(
      `[Scan] scan started for ${state.selectedPaths.length} folder(s): ` +
      state.selectedPaths.map((p) => `"${p}"`).join(', ') +
      ` incremental=${isRescan} ts=${new Date().toISOString()}`,
    );
    try {
      summary = await scanLibrary(state.selectedPaths, scanSources);
    } catch (e: any) {
      // Older Connector versions return Vec<ScannedTrack> — fall back to legacy path
      const legacyResult = e as any;
      if (Array.isArray(legacyResult)) {
        console.info('[scan-coordinator] Legacy Connector detected — falling back to array ingest');
        setState({ phase: 'ingesting', ingestTotal: legacyResult.length, ingestDone: 0 });
        streamIngestedCount = 0;
        totalFailedBatches = await ingestChunk(legacyResult, sourceId, machineId);
        summary = {
          total_tracks: legacyResult.length,
          total_albums: state.nativeProgress?.albums_identified || 0,
          duration_secs: 0,
        };
      } else {
        throw e;
      }
    }

    // Wait for all streaming ingestion to complete
    await ingestChain;

    if (unlistenNative) { unlistenNative(); unlistenNative = null; }
    if (unlistenBatch) { unlistenBatch(); unlistenBatch = null; }

    if (cancelRequested || state.nativeProgress?.phase === 'cancelled') {
      const albums = await fetchRealAlbumCount(summary.total_albums);
      setState({
        phase: 'cancelled',
        trackCount: summary.total_tracks,
        albumCount: albums,
        active: false,
      });
      // Still rebuild albums for whatever was ingested
      try { await libraryService.rebuildAlbums(); } catch {}
      invalidateLibraryStore();
      if (scanOperationId) {
        const cancelledId = scanOperationId;
        useOperationProgressStore.getState().updateOperation(cancelledId, {
          label: 'Scan cancelled',
          phase: 'cancelled',
          progress: 1,
        });
        scanOperationId = null;
        window.setTimeout(() => {
          useOperationProgressStore.getState().endOperation(cancelledId);
        }, 3000);
      }
      return;
    }

    if (totalFailedBatches > 0) {
      console.warn(`[scan-coordinator] ${totalFailedBatches} batch(es) failed during cloud ingest`);
    }

    try {
      await libraryService.rebuildAlbums();
    } catch (e: any) {
      console.warn('[scan-coordinator] album rebuild after ingest failed:', e?.message || e);
    }

    console.log(
      `[Scan] cloud scan complete: ${summary.total_tracks} tracks ts=${new Date().toISOString()}`,
    );
    await finishScanSuccess(summary, totalFailedBatches > 0);
  } catch (err: any) {
    console.error('[Scan] legacy/cloud scan failed:', err?.message || err, err);
    if (unlistenNative) {
      try { unlistenNative(); } catch {}
      unlistenNative = null;
    }
    if (unlistenBatch) {
      try { unlistenBatch(); } catch {}
      unlistenBatch = null;
    }
    setState({
      phase: 'error',
      error: err?.message || 'Scan failed',
      active: false,
    });
    if (scanOperationId) {
      const errId = scanOperationId;
      useOperationProgressStore.getState().updateOperation(errId, {
        label: err?.message || 'Scan failed',
        phase: 'error',
        progress: 1,
      });
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(errId);
      }, 4000);
    }
  }
}

function invalidateLibraryStore(): void {
  try {
    useLibraryStore.getState().invalidate();
  } catch { /* store may not be available */ }
  try {
    // Bust version-only cache hits — local diff returns metadata rows, not albums.
    useLibraryCacheStore.getState().setVersion(null);
  } catch { /* cache store optional */ }
}

async function fetchRealAlbumCount(fallback: number): Promise<number> {
  try {
    const stats = await libraryService.getStats();
    const n = (stats as any)?.total_albums ?? (stats as any)?.albums;
    if (stats?.success && typeof n === 'number' && n > 0) return n;
  } catch { /* use fallback */ }
  return fallback;
}
